// ═══════════════════════════════════════════════════════════
// Auth Store — HTTP-only cookie auth
//
// GAB tokens live in HttpOnly cookies (set server-side).
// This store holds only user identity + incident context.
// V1 compat: token field kept as null (all API calls now
// route through /api/v2proxy which injects auth server-side).
// ═══════════════════════════════════════════════════════════

import { create } from 'zustand';
import { gabLogin, gabLogout } from '../api/client';
import { saveUser, loadUser, clearUser } from './session';
import { storeToken, getToken, storeOidcUser, loadOidcUser, clearOidcSession } from './token';
import type { OidcTokens } from './oidc';
import type { V2User } from '../api/types';

const INCIDENT_KEY = 'ogemc_activeIncident';

export interface Incident {
  id: number;
  name: string;
  type?: string;
  incidentType?: string;
  status?: string;
  location?: string;
  latitude?: string;
  longitude?: string;
  state?: string;
  county?: string;
  city?: string;
  startDate?: string;
  endDate?: string;
  description?: string;
  commander?: string;
  severity?: string;
  createdByName?: string;
  createdByEmail?: string;
  datecreated?: string;
}

export interface TableRight {
  tableKey: string;
  canView: boolean;
  canCreate: boolean;
  canUpdate: boolean;
  canDelete: boolean;
}

interface AuthState {
  user: V2User | null;
  isAuthenticated: boolean;
  sessionChecked: boolean;

  // V1 compat — always null; API auth is cookie-based now
  token: null;
  isLoading: boolean;
  error: string | null;
  rights: TableRight[];

  // Active incident context
  activeIncident: Incident | null;
  incidents: Incident[];

  // Actions
  login: (email: string, password: string) => Promise<void>;
  loginWithOidc: (tokens: OidcTokens) => Promise<void>;
  logout: () => void;
  restoreSession: () => boolean;
  setActiveIncident: (incident: Incident | null) => void;
  setIncidents: (incidents: Incident[]) => void;
  loadIncidents: () => Promise<void>;
  hasRight: (tableKey: string, action: 'view' | 'update' | 'create' | 'delete') => boolean;
}

export const useAuthStore = create<AuthState>((set, get) => {
  // Restore cached user identity immediately (no token check yet)
  const cachedUser = loadUser();

  // Restore OIDC session if token is present in sessionStorage
  const oidcUser = loadOidcUser();
  const hasOidcToken = !!getToken();
  if (oidcUser && hasOidcToken) {
    // OIDC session — token is the source of truth; no cookie check needed
  } else if (oidcUser && !hasOidcToken) {
    // Token expired/cleared — clear stored user
    clearOidcSession();
  }

  return {
    user: oidcUser as V2User | null ?? cachedUser,
    isAuthenticated: (oidcUser && hasOidcToken) ? true : !!cachedUser,
    sessionChecked: true,
    token: null,
    isLoading: false,
    error: null,
    rights: [],
    activeIncident: null,
    incidents: [],

    login: async (email, password) => {
      set({ isLoading: true, error: null });
      try {
        const { user } = await gabLogin(email, password);
        saveUser(user);
        set({
          user,
          isAuthenticated: true,
          sessionChecked: true,
          isLoading: false,
          token: null,
        });
        await get().loadIncidents();
        try {
          const { usePermissionsStore } = await import('../store/permissionsStore');
          await usePermissionsStore.getState().load(user.id);
        } catch (e) { console.warn('loadPermissions failed:', e); }
      } catch (err: unknown) {
        const msg = err instanceof Error ? err.message : 'Login failed';
        set({ isLoading: false, error: msg });
        throw err;
      }
    },

    loginWithOidc: async (tokens) => {
      set({ isLoading: true, error: null });
      try {
        const { parseIdToken } = await import('./oidc');
        const claims = parseIdToken(tokens.idToken);
        const user: V2User = {
          id:        claims.sub,
          email:     claims.email,
          fullName:  claims.name ?? `${claims.given_name ?? ''} ${claims.family_name ?? ''}`.trim(),
          firstName: claims.given_name ?? (claims.name ?? '').split(' ')[0] ?? '',
          lastName:  claims.family_name ?? (claims.name ?? '').split(' ').slice(1).join(' ') ?? '',
          role:      claims.role ?? '',
          isAdmin:   claims.role === 'super_admin' || claims.role === 'admin',
        };
        storeToken(tokens.accessToken);
        storeOidcUser(user);
        set({ user, isAuthenticated: true, sessionChecked: true, isLoading: false, token: null });
        await get().loadIncidents();
        try {
          const { usePermissionsStore } = await import('../store/permissionsStore');
          await usePermissionsStore.getState().load(user.id);
        } catch (e) { console.warn('loadPermissions failed:', e); }
      } catch (err: unknown) {
        const msg = err instanceof Error ? err.message : 'Login failed';
        set({ isLoading: false, error: msg });
        throw err;
      }
    },

    logout: () => {
      gabLogout();
      clearOidcSession();
      clearUser();
      try { localStorage.removeItem(INCIDENT_KEY); } catch {}
      set({
        user: null,
        token: null,
        isAuthenticated: false,
        activeIncident: null,
        incidents: [],
      });
    },

    // restoreSession: kept for callers — the store already self-restores on init
    restoreSession: () => {
      const user = loadUser();
      if (user) {
        set({ user, isAuthenticated: true });
        try {
          const stored = localStorage.getItem(INCIDENT_KEY);
          if (stored) set({ activeIncident: JSON.parse(stored) });
        } catch {}
        import('../store/permissionsStore').then(({ usePermissionsStore }) => {
          usePermissionsStore.getState().load(user.id).catch(e => console.warn('loadPermissions failed:', e));
        });
        return true;
      }
      set({ sessionChecked: true });
      return false;
    },

    setActiveIncident: (incident) => {
      set({ activeIncident: incident });
      try {
        if (incident) localStorage.setItem(INCIDENT_KEY, JSON.stringify(incident));
        else localStorage.removeItem(INCIDENT_KEY);
      } catch {}
    },

    setIncidents: (incidents) => {
      set({ incidents });
      const current = get().activeIncident;
      if (!current && incidents.length > 0) {
        const active = incidents.find(i => i.status === 'active') || incidents[0];
        get().setActiveIncident(active);
      }
    },

    loadIncidents: async () => {
      try {
        const { useIncidentStore } = await import('../store/incident-store');
        await useIncidentStore.getState().fetch();
      } catch (e) {
        console.warn('loadIncidents failed:', e);
      }
    },

    hasRight: (_tableKey, _action) => {
      return !!get().user;
    },
  };
});
